import { useBehavior, useTriggerAction, useUpdateWidgetValue } from '@libs/core';
import { ActionActionTypeEnum, TWidget } from '@libs/types';
import React from 'react';
import { TSelectorRegularData } from '../../../types/data';
import { TRowOption, TSelectorRegularOption } from '../../../types/options';
import { SelectorRegularView } from './SelectorRegularView';

type TSelectorRegularWidgetProps = {
    widget: TWidget<TSelectorRegularData, TRowOption & TSelectorRegularOption>;
};

/**
 * Standalone SelectorRegular widget driven entirely by server-provided configuration.
 * Owns behavior hooks (useBehavior, useTriggerAction, useUpdateWidgetValue) and
 * delegates all rendering to SelectorRegularView.
 *
 * Use this component when the widget definition comes from the server and no
 * additional prop overrides are required.
 */
export const SelectorRegularWidget = ({ widget }: TSelectorRegularWidgetProps) => {
    const { updateWidgetValue } = useUpdateWidgetValue(widget);
    const { triggerActionsByType } = useTriggerAction(widget);
    const { isDisabled, isHidden } = useBehavior(widget);

    const handleAction = () => {
        updateWidgetValue(!widget.data.value);
        triggerActionsByType(ActionActionTypeEnum.onClick).then();
    };

    return (
        <SelectorRegularView
            uid={widget.uid}
            widget={widget}
            isDisabled={isDisabled}
            isHide={isHidden}
            handleClick={handleAction}
            variant={widget.options?.variant}
            title={widget.data?.messages?.title}
            subtitle={widget.data?.messages?.subtitle}
            isSelected={widget.data.value}
            metaText={widget.data?.messages?.metaText}
            avatar={widget.options?.avatar}
            iconName={widget.options?.iconName}
            flagIcon={widget.options?.flagIcon}
            iconVariant={widget.options?.iconVariant}
            iconHierarchy={widget.options?.iconHierarchy}
            metaVariant={widget.options?.metaVariant}
            metaHierarchy={widget.options?.metaHierarchy}
            margin={widget.options?.margin}
        />
    );
};
